import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Circle, Clock, Package, Truck, Home as HomeIcon, XCircle, ArrowLeft } from "lucide-react";
import api, { fmtJMD, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const FLOW = [
  { key: "pending", label: "Pending", icon: Clock },
  { key: "processing", label: "Package is processing", icon: Package },
  { key: "processing_complete", label: "Processing complete", icon: CheckCircle2 },
  { key: "in_transit", label: "Package is in transit", icon: Truck },
  { key: "delivered", label: "Package is delivered", icon: HomeIcon },
];

export default function Scan() {
  const { id } = useParams();
  const { user, hasPermission, loading } = useAuth();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const canScan = hasPermission("orders.scan");

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${id}/scan-info`);
      setInfo(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Could not load order");
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    if (!canScan) return;
    load();
    // eslint-disable-next-line
  }, [id, loading, user, canScan]);

  if (loading) return <div className="py-24 text-center text-zinc-500">Loading…</div>;
  if (!user)
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="scan-unauthenticated">
        <div className="overline text-zinc-500 mb-3">Scanner</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Sign in to update</h1>
        <p className="mt-3 text-zinc-600 text-sm">This scan link is for Fruit Blossoms staff. Please log in with your staff account.</p>
        <Link to="/login" className="btn-primary inline-flex mt-6">Staff login</Link>
      </div>
    );

  if (!canScan)
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <XCircle size={36} className="mx-auto text-red-500 mb-3" />
        <div className="font-display text-2xl font-bold">Not authorised</div>
        <p className="text-sm text-zinc-500 mt-2">Your account doesn't have the <code>orders.scan</code> permission.</p>
      </div>
    );

  if (err) return <div className="py-24 text-center text-red-500">{err}</div>;
  if (!info) return <div className="py-24 text-center text-zinc-500">Loading…</div>;

  const currentIdx = FLOW.findIndex((s) => s.key === info.status);

  const setStatus = async (status) => {
    setSaving(true);
    try {
      await api.post(`/orders/${id}/scan-update`, { status });
      toast.success(`Updated to: ${labelOf(status)}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-xl mx-auto px-6 py-10" data-testid="scan-page">
      <Link to="/admin/scanner" className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-6">
        <ArrowLeft size={12} /> Back to scanner
      </Link>

      <div className="rounded-2xl border border-zinc-200 bg-white p-6">
        <div className="overline text-zinc-500 mb-2">Order</div>
        <div className="font-mono text-sm text-zinc-500">{info.id}</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">{info.customer_name}</h1>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm mt-2 text-zinc-600">
          <span>{info.parish}</span>
          <span>·</span>
          <span>{info.delivery_day}</span>
          <span>·</span>
          <span>{info.items_count} item(s)</span>
          <span>·</span>
          <span className="font-semibold text-zinc-900">{fmtJMD(info.total)}</span>
        </div>

        <div className="mt-6 pt-6 border-t border-zinc-200">
          <div className="overline text-zinc-500 mb-4">Status workflow</div>
          <div className="space-y-3">
            {FLOW.map((step, i) => {
              const isDone = i <= currentIdx;
              const isCurrent = i === currentIdx;
              const StepIcon = step.icon;
              return (
                <button
                  key={step.key}
                  onClick={() => setStatus(step.key)}
                  disabled={saving}
                  className={`w-full flex items-center gap-3 p-4 rounded-xl border transition text-left ${
                    isCurrent ? "border-[#F74B64] bg-[#FFF5F6]" :
                    isDone ? "border-zinc-200 bg-white" : "border-zinc-200 bg-white opacity-80 hover:opacity-100"
                  }`}
                  data-testid={`scan-step-${step.key}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isDone ? "bg-[#F74B64] text-white" : "bg-zinc-100 text-zinc-400"
                  }`}>
                    <StepIcon size={16} />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{step.label}</div>
                    {isCurrent && <div className="text-[10px] uppercase tracking-widest font-bold text-[#F74B64]">Current</div>}
                  </div>
                  {isDone && <CheckCircle2 size={16} className="text-[#F74B64]" />}
                </button>
              );
            })}
            <button
              onClick={() => setStatus("cancelled")}
              disabled={saving}
              className="w-full flex items-center gap-3 p-3 rounded-xl border border-zinc-200 text-sm text-red-500 hover:bg-red-50"
              data-testid="scan-step-cancel"
            >
              <XCircle size={16} /> Cancel order
            </button>
          </div>
        </div>

        {info.status_history?.length > 1 && (
          <div className="mt-6 pt-6 border-t border-zinc-200">
            <div className="overline text-zinc-500 mb-3">History</div>
            <ul className="space-y-1 text-xs text-zinc-600">
              {info.status_history.slice().reverse().map((h, i) => (
                <li key={i} className="flex justify-between">
                  <span>{labelOf(h.status)} <span className="text-zinc-400">· {h.updated_by_name}</span></span>
                  <span className="text-zinc-400">{new Date(h.timestamp).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function labelOf(status) {
  const f = FLOW.find((s) => s.key === status);
  return f ? f.label : status;
}
